// =============================================================================
// src/features/reputation/reputation-data.ts
//
// Datasets ficticios + formatadores do takeover de Reputacao.
// Proveniencia: assets/reputation.js linhas 23-43 (POLS/SEGMENT_AVG/fmt/band),
//   92-93 (TREND), 183-188 (SOV), 220-226 (CHANNELS/CHAN_LEGEND), 247-251 (NEG),
//   289-294 (DIMS), 366-385 (ticker). Valores/cores/limiares COPIADOS byte-a-byte
//   (constraint de paridade do usuario). Politicos de Manaus, cores de partido do
//   sistema — dados fakes.
//
// SRP: so dados e os dois formatadores puros (fmt/band). Sem DOM, sem timers.
// =============================================================================

import type { ReputationChannelTotals } from './reputation-types';

/** Identificadores estaveis dos politicos no comparador/duelo (POLS[*].id). */
export type ReputationPolId = 'ricardo' | 'omar' | 'marina' | 'paulo' | 'diego';

/** Nomes das 4 faixas do Reputation Index — união canônica (reusada pelo overlay/copiloto). */
export type ReputationBandName = 'Crítico' | 'Regular' | 'Bom' | 'Excelente';

/** Faixa de classificacao do Reputation Index (retorno de band). */
export interface ReputationBand {
  readonly key: 'crit' | 'reg' | 'good' | 'exc';
  readonly name: ReputationBandName;
}

/** Chave de dimensao numerica de um politico (usada no duelo). */
export type ReputationDimKey = 'index' | 'nrs' | 'sov' | 'bps';

/** Um politico do segmento (index 0-100; nrs/sov/bps 0-100). */
export interface ReputationPol {
  readonly id: ReputationPolId;
  readonly name: string;
  readonly party: string;
  readonly color: string;
  readonly index: number;
  readonly nrs: number;
  readonly sov: number;
  readonly bps: number;
}

/** Politicos do segmento — assets/reputation.js 25-31. */
export const REPUTATION_POLS: readonly ReputationPol[] = [
  { id: 'ricardo', name: 'Ricardo Nogueira', party: 'PSD', color: '#378ADD', index: 76, nrs: 74, sov: 81, bps: 72 },
  { id: 'omar', name: 'Omar Aziz', party: 'PSD', color: '#1D9E75', index: 49, nrs: 63, sov: 94, bps: 50 },
  { id: 'marina', name: 'Marina Castro', party: 'PSDB', color: '#7F77DD', index: 44, nrs: 47, sov: 38, bps: 46 },
  { id: 'paulo', name: 'Paulo Tavares', party: 'União', color: '#BA7517', index: 33, nrs: 36, sov: 29, bps: 34 },
  { id: 'diego', name: 'Diego Lima', party: 'PT', color: '#E24B4A', index: 16, nrs: 18, sov: 14, bps: 21 },
];

/** Media do segmento usada na linha tracejada do ranking — assets/reputation.js 32. */
export const REPUTATION_SEGMENT_AVG = 42;

/**
 * Formata um numero no padrao pt-BR do mockup: arredonda a 2 casas, mantem inteiro
 * sem decimais e troca o ponto por virgula. Porte FIEL de `fmt` (reputation.js 34-37).
 */
export function formatReputationNumber(rawValue: number): string {
  const rounded = Math.round(rawValue * 100) / 100;
  const text = Number.isInteger(rounded) ? rounded.toString() : rounded.toFixed(2);
  return text.replace('.', ',');
}

/** Classifica um valor 0-100 na faixa do indice — porte FIEL de `band` (reputation.js 38-43). */
export function reputationBand(value: number): ReputationBand {
  if (value < 25) return { key: 'crit', name: 'Crítico' };
  if (value < 50) return { key: 'reg', name: 'Regular' };
  if (value < 75) return { key: 'good', name: 'Bom' };
  return { key: 'exc', name: 'Excelente' };
}

/** Serie historica do indice (eixo Y) — assets/reputation.js 92. */
export const REPUTATION_TREND: readonly number[] = [61, 64, 60, 57, 55, 58, 56, 52, 54, 53.15];

/** Rotulos da serie historica (leitura por teclado) — assets/reputation.js 93. */
export const REPUTATION_TREND_LABELS: readonly string[] = [
  '27 jul', '7 ago', '21 ago', '4 set', '18 set', '2 out', '16 out', '30 out', '13 nov', 'agora',
];

/** Fatia do donut de Share of Voice. */
export interface ReputationSovSlice {
  readonly nm: string;
  readonly sub: string;
  readonly color: string;
  readonly pct: number;
}

/** Donut Share of Voice — assets/reputation.js 184-188. */
export const REPUTATION_SOV: readonly ReputationSovSlice[] = [
  { nm: 'Ricardo Nogueira', sub: 'PSD', color: '#378ADD', pct: 38 },
  { nm: 'Omar Aziz', sub: 'PSD · você', color: '#1D9E75', pct: 27 },
  { nm: 'Marina Castro', sub: 'PSDB', color: '#7F77DD', pct: 21 },
  { nm: 'Outros', sub: 'demais nomes', color: '#B8B7B0', pct: 14 },
];

/** Segmento [cor, larguraPct] de uma barra de canal. */
export type ReputationChannelSeg = readonly [color: string, widthPct: number];

/** Linha de barras por canal. */
export interface ReputationChannel {
  readonly name: string;
  readonly icon: string;
  readonly total: string;
  readonly segs: readonly ReputationChannelSeg[];
}

/** Barras por canal — assets/reputation.js 221-224. */
export const REPUTATION_CHANNELS: readonly ReputationChannel[] = [
  { name: 'Mídias online', icon: 'ti-world', total: '9,4 mil', segs: [['#378ADD', 34], ['#1D9E75', 29], ['#7F77DD', 20], ['#B8B7B0', 17]] },
  { name: 'Mídias offline', icon: 'ti-broadcast', total: '1,8 mil', segs: [['#378ADD', 41], ['#1D9E75', 22], ['#7F77DD', 19], ['#B8B7B0', 18]] },
  { name: 'Redes sociais', icon: 'ti-brand-x', total: '3,1 mil', segs: [['#378ADD', 36], ['#1D9E75', 31], ['#7F77DD', 18], ['#B8B7B0', 15]] },
  { name: 'Podcast', icon: 'ti-microphone-2', total: '0,7 mil', segs: [['#378ADD', 44], ['#1D9E75', 24], ['#7F77DD', 16], ['#B8B7B0', 16]] },
];

/** Legenda das barras por canal [cor, rotulo] — assets/reputation.js 226. */
export const REPUTATION_CHAN_LEGEND: readonly ReputationChannelSeg2[] = [
  ['#378ADD', 'Ricardo'], ['#1D9E75', 'Omar'], ['#7F77DD', 'Marina'], ['#B8B7B0', 'Outros'],
];

/** Item de legenda [cor, rotulo] das barras por canal. */
export type ReputationChannelSeg2 = readonly [color: string, label: string];

/** Series de mencoes negativas por nome — assets/reputation.js 247-251. */
export const REPUTATION_NEG: ReputationChannelTotals = {
  helena: [120, 180, 140, 220, 760, 880, 540, 420],
  ricardo: [300, 280, 260, 240, 220, 210, 230, 250],
  marina: [90, 110, 130, 120, 160, 150, 140, 170],
};

/** Dimensao do duelo 1-a-1. */
export interface ReputationDuelDim {
  readonly key: ReputationDimKey;
  readonly label: string;
  readonly tag: string;
  readonly headline?: boolean;
}

/** Dimensoes do duelo — assets/reputation.js 290-294. */
export const REPUTATION_DUEL_DIMS: readonly ReputationDuelDim[] = [
  { key: 'index', label: 'Reputation Index', tag: '', headline: true },
  { key: 'nrs', label: 'Percepção', tag: 'NRS' },
  { key: 'sov', label: 'Presença', tag: 'SOV' },
  { key: 'bps', label: 'Protagonismo', tag: 'BPS' },
];

/** Velocidade do ticker em px/s — assets/reputation.js 365. */
export const REPUTATION_TICKER_PXPS = 45;

/** Item do ticker financeiro: noticia ou metrica. */
export type ReputationTickerItem =
  | { readonly kind: 'news'; readonly src: string; readonly text: string; readonly sent: string; readonly time: string }
  | { readonly kind: 'metric'; readonly label: string; readonly value: string; readonly dir: string };

/** Conteudo do ticker — assets/reputation.js 368-378 (helpers tNews/tMetric inlinados). */
export const REPUTATION_TICKER_DATA: readonly ReputationTickerItem[] = [
  { kind: 'news', src: 'Portal Amazonas Agora', text: 'edital da reforma do pronto-socorro vira pauta no Senado', sent: 'neg', time: '08h40' },
  { kind: 'metric', label: 'Reputation Index', value: '53,15', dir: 'down-bad' },
  { kind: 'news', src: 'Rádio Cidade FM', text: 'destaca novo posto de saúde entregue na Zona Norte', sent: 'pos', time: '09h10' },
  { kind: 'metric', label: 'Share of Voice', value: '27%', dir: '' },
  { kind: 'news', src: '@base_zonaleste', text: '“finalmente um mandato que mostra os números da obra”', sent: 'pos', time: '09h22' },
  { kind: 'metric', label: 'Sentimento', value: '+11', dir: 'up-good' },
  { kind: 'news', src: 'Diário do Amazonas', text: 'compara reputação dos parlamentares no semestre', sent: 'neu', time: '10h05' },
  { kind: 'metric', label: 'Menções 24h', value: '1.240', dir: 'up-bad' },
  { kind: 'news', src: 'Podcast Manaus Hoje', text: 'cita transparência do gabinete como referência', sent: 'pos', time: '10h31' },
];
